"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelDrawableLabel = void 0;
const tslib_1 = require("tslib");
const mobx_1 = require("mobx");
const __1 = require("../..");
const draw_1 = require("../../../draw");
const label_draw_svg_1 = require("../../../draw/drawable/label.draw.svg");
const capture_1 = require("../../capture");
const drawable_1 = require("./drawable");
const marker_drawable_1 = require("./marker.drawable");
const marker_offset_drawable_1 = require("./marker.offset.drawable");
/**
 * Label has rows of text.
 * Label is attached to a parent Marker or Line, identified by "parentId".
 */
class CollectionModelDrawableLabel extends drawable_1.MoveableDrawable {
    constructor(collectionModelDrawableState, drawableId, x, y, parentId, text = [], links = []) {
        super(collectionModelDrawableState, drawableId);
        this.x = x;
        this.y = y;
        this._parentId = parentId;
        this._text = text;
        this._links = links;
    }
    static isLabel(t) {
        return t instanceof CollectionModelDrawableLabel;
    }
    /**
     * Move the label to this location
     *
     * @param x coordinates in pixels
     * @param y coordinates in pixels
     */
    move(x, y) {
        const bounds = this.getPointInBounds(x, y);
        this.x = bounds.x;
        this.y = bounds.y;
    }
    get parentId() {
        return this._parentId;
    }
    get parent() {
        return this.drawableState.getDrawable(this._parentId);
    }
    /**
     * @deprecated Use {@link getDisplayText} instead.
     */
    get text() {
        if (this.parent != null) {
            if (marker_drawable_1.CollectionModelDrawableMarker.isBase(this.parent)) {
                if (this.drawableState.baseOffset == null) {
                    return ['Base'];
                }
                return ['Base offset'];
            }
            if (marker_offset_drawable_1.CollectionModelDrawableMarkerOffset.isBaseOffset(this.parent)) {
                return ['Base'];
            }
        }
        return this._text;
    }
    /**
     * @deprecated Use {@link getDisplayText} instead.
     */
    set text(value) {
        this._text = value;
    }
    getDisplayText(options) {
        if (marker_drawable_1.CollectionModelDrawableMarker.isBase(this.parent)) {
            if (this.drawableState.baseOffset == null) {
                return ['Base'];
            }
            return ['Base offset'];
        }
        if (marker_offset_drawable_1.CollectionModelDrawableMarkerOffset.isBaseOffset(this.parent)) {
            return ['Base'];
        }
        if (this.parent instanceof __1.CollectionModelDrawableVerticalSeparation) {
            return this.parent.getLabelDisplayText(options);
        }
        return [];
    }
    /** Only height & vertical separation fields can be linked to */
    get links() {
        if (!Array.isArray(this._links)) {
            return [];
        }
        const output = [];
        const collection = this.drawableState.collection;
        for (const link of this._links) {
            const collectionField = collection.findField(link);
            if (collectionField == null) {
                continue;
            }
            output.push(collectionField);
        }
        return output;
    }
    linkHeight(field) {
        var _a;
        if (!this.parent || !(this.parent instanceof marker_drawable_1.CollectionModelDrawableMarker)) {
            throw new Error(`Attempted to link unlinkable label "${this.id}" (parent must be Marker)`);
        }
        const links = this._links.slice();
        links.push(field.id);
        this._links = links;
        if (((_a = field.stateValue) === null || _a === void 0 ? void 0 : _a.drawable) !== this.parentId) {
            field.Mutator.drawable(this.parent);
        }
    }
    linkVerticalSeparation(field) {
        var _a, _b;
        if (!this.parent || !(this.parent instanceof marker_drawable_1.CollectionModelDrawableMarker)) {
            throw new Error(`Attempted to link unlinkable label "${this.id}" (parent must be Marker)`);
        }
        const links = this._links.slice();
        links.push(field.id);
        this._links = links;
        if (((_a = field.stateValue) === null || _a === void 0 ? void 0 : _a.drawableTop) !== this.parentId && ((_b = field.stateValue) === null || _b === void 0 ? void 0 : _b.drawableBottom) !== this.parentId) {
            field.Mutator.drawable(this.parent);
        }
    }
    unlink(fieldId) {
        const fieldIdIndex = this._links.indexOf(fieldId);
        if (fieldIdIndex === -1) {
            return;
        }
        const links = this._links.slice();
        links.splice(fieldIdIndex, 1);
        this._links = links;
        // If this was the last link to be removed, automatically remove the label as well.
        if (this._links.length === 0) {
            this.remove();
        }
    }
    snapToLeftSide() {
        this.move(0, this.y);
    }
    snapToRightSide() {
        var _a, _b;
        this.move(((_b = (_a = this.drawableState.capture) === null || _a === void 0 ? void 0 : _a.resolution.width) !== null && _b !== void 0 ? _b : capture_1.DEFAULT_CAPTURE_SIZE.width) - label_draw_svg_1.LABEL_SIZE.width * draw_1.DEFAULT_SCALE, this.y);
    }
    remove() {
        var _a;
        super.remove();
        // Remove this label's parent too, unless it has already been removed
        if (this.drawableState.hasDrawable(this._parentId)) {
            (_a = this.parent) === null || _a === void 0 ? void 0 : _a.remove();
        }
        // Mutate the linked collection fields to remove their references to this label
        for (const link of this.links) {
            link.Mutator.unlink();
        }
        this._links = [];
    }
    get isInsightMeasurement() {
        return this.parent.isInsightMeasurement;
    }
    isApprovableInsightDrawable() {
        return true;
    }
    /**
     * Shortcut to check if the insight detection class is a base offset marker.
     */
    get isInsightBaseOffsetDetectionClass() {
        if (this.parent instanceof marker_drawable_1.CollectionModelDrawableMarker) {
            return this.parent.isInsightBaseOffsetDetectionClass;
        }
        return false;
    }
    approveInsightFlag() {
        if (!this.parent.isApprovableInsightDrawable()) {
            return;
        }
        this.parent.approveInsightFlag();
    }
    get isVisible() {
        return this.parent.isVisible;
    }
    clone(newParentDrawableState) {
        return new CollectionModelDrawableLabel(newParentDrawableState, this.id, this.x, this.y, this.parentId, this._text, this._links);
    }
    equals(other) {
        return (other instanceof CollectionModelDrawableLabel &&
            this.x === other.x &&
            this.y === other.y &&
            this._text.join('') === other._text.join('') &&
            this._links.join('') === other._links.join('') &&
            this._parentId === other._parentId &&
            this._equals(other));
    }
}
exports.CollectionModelDrawableLabel = CollectionModelDrawableLabel;
tslib_1.__decorate([
    mobx_1.observable
], CollectionModelDrawableLabel.prototype, "x", void 0);
tslib_1.__decorate([
    mobx_1.observable
], CollectionModelDrawableLabel.prototype, "y", void 0);
tslib_1.__decorate([
    mobx_1.action
], CollectionModelDrawableLabel.prototype, "move", null);
tslib_1.__decorate([
    mobx_1.observable
], CollectionModelDrawableLabel.prototype, "_text", void 0);
tslib_1.__decorate([
    mobx_1.action
], CollectionModelDrawableLabel.prototype, "linkHeight", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionModelDrawableLabel.prototype, "linkVerticalSeparation", null);
tslib_1.__decorate([
    mobx_1.action
], CollectionModelDrawableLabel.prototype, "unlink", null);
//# sourceMappingURL=label.drawable.js.map